import React, { useEffect, useState } from 'react'
import GetAppIcon from '@mui/icons-material/GetApp';
import { useForm } from 'react-hook-form';
import { isAfter } from 'date-fns';
import { useDispatch } from 'react-redux';
import { IconButton } from '@mui/material';
import { toast } from 'react-toastify';

//Api
import { invoiceApi } from '@/main/api';

//Services
import { getDefaultCountryOptionDrop, getTranslateByKey, isNullUndefined, removeEmptyValues } from '@/main/services/utils';
import { downloadFile } from '@/main/services/utilsFile';
import { convertStringForBoolean } from '@/main/services/utilsString';
import { ReasonTypeEnum } from '@/main/services/enum/ReasonEnum';
import { validateFormatDate } from '@/main/services/utilsDate';

//Reducer
import { getReason, resetReducerReason, useReasonReducer } from '@/main/store/reason';
import { getCountriesUser, useCountryReducer } from '@/main/store/country';

//Type
import { InvoiceFileDataResponse } from '@/main/models/response';
import { KeyValue, KeyValueDefault } from '@/main/models/common';
import { EditInvoiceRequest, ReasonFilterRequest } from '@/main/models/request';

//Components
import SecondaryButton from '@/main/components/shared/button/secondaryButton';
import ColumnLabel from '@/main/components/common/columnLabel/columnLabel';
import KOGFileUploader from '@/main/components/shared/fileUploader/fileUploader';
import KogInputTextControlledNew from '@/main/components/shared/inputText/inputTextControlledNew';
import KogKeyboardDatePicker from '@/main/components/shared/datepicker/keyboardDatePicker';
import KogAutoCompleteControlled from '@/main/components/shared/inputAutoComplete/autoCompleteControlled';

//Styles
import { DateContainerDefault } from '@/main/components/shared/styles/dateStyles';

// Styles and local components
import *  as S from './styles';

interface IInvoceEditForm {
  invoiceNumber: string | null;
  issueDate: Date;
  reason: KeyValue | KeyValueDefault | null;
  observation: string;
}

interface IModalEditInvoice {
  isEdit: boolean;
  guidEditInvoice: string | null;
  invoiceNumber: string | null;
  issueDate: Date;
  supplierName: string | null;
  invoicesFile: InvoiceFileDataResponse[];
  creationDate: Date;
  handleEdit: (
    isEdit: boolean,
    guid: string,
    invoiceNumber: string,
    issueDate: Date,
    supplierName: string,
    invoicesFile: InvoiceFileDataResponse[],
    creationDate: Date
  ) => void;
}

function ModalEditInvoice({
  isEdit,
  guidEditInvoice,
  invoiceNumber,
  issueDate,
  supplierName,
  invoicesFile,
  creationDate,
  handleEdit
}: IModalEditInvoice) {
  //#region Reducer
  const dispatch = useDispatch();
  const reasonReducer = useReasonReducer();
  const countryReducer = useCountryReducer()
  //#endregion;

  //#region States
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [changeIssueDate, setChangeIssueDate] = useState<Date | number>(0);
  const [files, setFiles] = useState<File[]>([]);
  const [selectedReason, setSelectedReason] = useState<KeyValueDefault | null>(null);
  const [guidsFileRemoved, setGuidsFileRemoved] = useState<string[]>([]);
  const [quoteCreationDate, setQuoteCreationDate] = useState<Date | number>(creationDate || 0);
  const [mensageErrorIssueDate, setMensageErrorIssueDate] = useState<string | null>(null);
  //#endregion;

  //#region React Hook Form
  const INITIAL_FORM_STATE = {
    invoiceNumber: '' as string,
    issueDate: new Date(),
    reason: null as KeyValue | KeyValueDefault | null,
    observation: ''
  };
  const {
    control,
    handleSubmit,
    watch,
    formState: { errors },
    setError,
    setValue,
    clearErrors
  } = useForm<IInvoceEditForm>({
    mode: 'all',
    defaultValues: INITIAL_FORM_STATE
  });

  const watcherIssueDate = watch('issueDate');
  //#endregion;

  function functionSubmit(dataForm: IInvoceEditForm) {
    if (quoteCreationDate && dataForm.issueDate) {
      if (isAfter(new Date(quoteCreationDate).getTime(), new Date(dataForm.issueDate).getTime())) {
        setError('issueDate', { type: 'issueDateMustNotBeLessThanTheOrderDate' });
        setMensageErrorIssueDate(getTranslateByKey('information.issueDateMustNotBeLessThanTheOrderDate', true));
        return true;
      }
    }

    setIsLoading(true);
    const uploadFilesData: File[] = files.filter(item => !item.type || item.type !== 'fileData');

    const requestIssueDate = new Date(dataForm.issueDate).setHours(0, 0, 0, 0);

    const request: EditInvoiceRequest = removeEmptyValues({
      guidInvoice: String(guidEditInvoice),
      invoiceNumber: String(dataForm.invoiceNumber),
      issueDate: new Date(requestIssueDate),
      guidReason: String(dataForm.reason?.key),
      reasonDescription: dataForm.observation,
      guidFilesToDelete: guidsFileRemoved,
      uploadFiles: uploadFilesData
    })
    invoiceApi.editinvoice(request)
      .then(() => {
        handleEdit(false, '', '', new Date(), '', [], new Date());
        toast.success(getTranslateByKey('information.savedSuccess'));
        setGuidsFileRemoved([]);
      })
      .finally(() => {
        setIsLoading(false);
      })

  }

  interface IDataFile {
    guidInvoice: string;
    name: string;
    type: string;
  }

  function handleDownloadFile(datafile: IDataFile) {

    invoiceApi
      .getfilebyguidinvoicefile(datafile.guidInvoice)
      .then((response) => {
        if (response.data.result) {
          const base64File = response.data.result;
          const nameFile = `${base64File.fileName}.${base64File.fileExtension.toLowerCase()}`
          downloadFile(base64File.file, nameFile);
        }
      })
      .finally(() => {
      })
  }

  //#region Function
  const isDisabledObservation = () => convertStringForBoolean(selectedReason?.default);

  const optionDefaultCountryDrop = getDefaultCountryOptionDrop(
    countryReducer.countriesUser.data
  );
  //#endregion;

  //#region useEffect
  useEffect(() => {
    if (!isDisabledObservation()) {
      setValue('observation', '');
    }
  }, [isDisabledObservation()]);

  useEffect(() => {
    const newFiles = invoicesFile.map(item => {
      return {
        name: `${item.fileName}.${item.fileExtension.toLowerCase()}`,
        type: 'fileData',
        guidInvoice: item.guidInvoiceFile
      }
    })
    setFiles(newFiles);
  }, [JSON.stringify(invoicesFile)]);

  useEffect(() => {
    if (optionDefaultCountryDrop !== null && isEdit) {
      const requestReason: ReasonFilterRequest = {
        guidCountry: String(optionDefaultCountryDrop?.key),
        idReasonType: ReasonTypeEnum.EditInvoice
      };
      dispatch(getReason(requestReason));
    }
  }, [optionDefaultCountryDrop, isEdit]);

  useEffect(() => {
    if (isEdit && guidEditInvoice) {
      clearErrors();
      setMensageErrorIssueDate(null);
      setValue('invoiceNumber', invoiceNumber);
      setValue('issueDate', new Date(issueDate));
      setValue('reason', null);
      setValue('observation', '');
      setChangeIssueDate(new Date(issueDate).setHours(0, 0, 0, 0));
      return;
    }
    if (!isEdit) {
      setGuidsFileRemoved([]);
      setValue('invoiceNumber', null);
      dispatch(resetReducerReason());
      setValue('reason', null);
      setValue('observation', '');
      setMensageErrorIssueDate(null);
      clearErrors();
    }
  }, [isEdit, guidEditInvoice]);

  useEffect(() => {
    if (creationDate) {
      setQuoteCreationDate(new Date(creationDate).setHours(0, 0, 0, 0));
    }
  }, [creationDate])

  useEffect(() => {
    dispatch(getCountriesUser());
  }, []);
  //#endregion;

  return (
    <>
      <form
        onSubmit={handleSubmit(functionSubmit)}
        noValidate
      >
        <S.Container>
          <ColumnLabel
            label={<S.AccordionTitle>{supplierName}</S.AccordionTitle>}
            content={
              <S.BoxInput>
                <KogInputTextControlledNew
                  name="invoiceNumber"
                  label={getTranslateByKey('information.invoiceNumber')}
                  control={control}
                  controllerProps={{ rules: { required: true, maxLength: 200 } }}
                  errorMessageCustom={
                    errors.invoiceNumber?.type === 'required'
                      ? getTranslateByKey('information.invoiceNumberRequired', true)
                      : ''
                  }

                  id="id-modalEditInvoice-txt-invoiceNumber"
                />
                <DateContainerDefault>
                  <KogKeyboardDatePicker
                    name="issueDate"
                    required
                    label={getTranslateByKey('information.issueDate')}
                    value={watcherIssueDate}
                    isFieldInModal
                    error={!!errors.issueDate || !!mensageErrorIssueDate}
                    helperText={mensageErrorIssueDate}
                    onChange={date => {
                      setChangeIssueDate(new Date(date).setHours(0, 0, 0, 0));
                      if (date instanceof Date) {
                        setValue('issueDate', date);
                      }
                      if
                        ((quoteCreationDate && date) &&
                        isAfter(new Date(quoteCreationDate).getTime(), new Date(date).getTime())
                      ) {
                        setError('issueDate', { type: 'issueDateMustNotBeLessThanTheOrderDate' });
                        return setMensageErrorIssueDate(getTranslateByKey('information.issueDateMustNotBeLessThanTheOrderDate', true));
                      }

                      if (!isNullUndefined(date)) {
                        clearErrors('issueDate');
                        setMensageErrorIssueDate(null);
                      }
                      if (isNullUndefined(date)) {
                        setError('issueDate', { type: 'required' });
                        return setMensageErrorIssueDate(getTranslateByKey('information.issueDateRequired', true));
                      }
                    }}
                    onBlur={() => {
                      if (!validateFormatDate(changeIssueDate) && !mensageErrorIssueDate) {
                        setError('issueDate', { type: 'invalidDateFormat' })
                        return setMensageErrorIssueDate(getTranslateByKey('information.invalidDateFormat', true));
                      }
                    }}
                    id="id-modalEditInvoice-date-issueDate"
                  />
                </DateContainerDefault>
                <KogAutoCompleteControlled
                  id="id-modalEditInvoice-autocomplete-reason"
                  className="ignore-click"
                  required
                  label={getTranslateByKey('information.reason')}
                  nameCustom="reason"
                  errorMessageCustom=""
                  control={control}
                  setValueCustom={setValue}
                  options={reasonReducer.reason.data}
                  isLoading={reasonReducer.reason.isLoading}
                  rulesCustom={{ required: true }}
                  hasErrorCustom={false}
                  onChangeCustom={(optionSelected: KeyValueDefault) => {
                    setSelectedReason(optionSelected);
                    setValue('reason', optionSelected);
                  }}
                />
                {isDisabledObservation() && (
                  <KogInputTextControlledNew
                    id="id-modalEditInvoice-text-observation"
                    control={control}
                    name="observation"
                    label={getTranslateByKey('information.observation')}
                    required
                    errorMessageCustom={
                      errors.observation?.type ===
                        'required'
                        ? getTranslateByKey(
                          'information.observationRequired'
                        )
                        : ''
                    }
                    controllerProps={{ rules: { required: true } }}
                  />
                )}
              </S.BoxInput>
            }
          />

          <ColumnLabel
            label={<S.AccordionTitle>{getTranslateByKey('information.uploadInvoices')}</S.AccordionTitle>}
            content={
              <KOGFileUploader
                isFullScreenDragDrop
                maxFileByType={{
                  xml: 1,
                  pdf: 1
                }}
                files={files}
                setFiles={setFiles}
                maxFiles={2}
                typeFiles={['invoices']}
                renderIcons={(fileIcon) => {
                  if (fileIcon.type === "fileData") {
                    return <IconButton
                      id={`id-dragDrop-btn-donwloadPreview-${fileIcon.name}`
                      }
                      onClick={() => handleDownloadFile(fileIcon)}
                    >
                      <GetAppIcon fontSize='medium' />
                    </IconButton>
                  }
                }
                }
                onCustomDelete={(nameFile) => {
                  const findGuidFile = invoicesFile.find(item => `${item.fileName}.${item.fileExtension.toLowerCase()}` === nameFile);
                  const findGuidFileRemoved = guidsFileRemoved.find(item => item === findGuidFile?.guidInvoiceFile);
                  if (!isNullUndefined(nameFile) && !isNullUndefined(findGuidFile) && isNullUndefined(findGuidFileRemoved)) {
                    setGuidsFileRemoved(prev => [...prev, findGuidFile?.guidInvoiceFile]);
                  }
                }}
              />
            }
          />

          <S.ContainerButton>
            <SecondaryButton
              id='id-modalEditInvoice-btn-back'
              onClick={() => {
                handleEdit(false, '', '', new Date(), '', [], new Date());
              }}
            >
              {getTranslateByKey('information.back')}
            </SecondaryButton>
            <SecondaryButton
              id='id-modalEditInvoice-btn-save'
              type='submit'
              isLoading={isLoading}
              disabled={isLoading || files.length == 0}
            >
              {getTranslateByKey('information.save')}
            </SecondaryButton>
          </S.ContainerButton>
        </S.Container>
      </form>
    </>
  )
}

export default ModalEditInvoice